import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';

const DESKTOP_MIN_WIDTH = 1024; // the app is in mobile layout below lg (1024px)

// Full-screen overlay. `inset: 0` fills the viewport now that the panel is no
// longer trapped in the header's backdrop-blur (the blur was moved off the
// badge's ancestors in Header.tsx). `width/height: auto` + `max-*: none` let
// inset define the size and drop the SDK's width cap. Inline + !important so
// they win over the SDK's own stylesheet; `transition: none` stops any slide.
const ROOT_STYLES: Record<string, string> = {
  position: 'fixed',
  inset: '0',
  width: 'auto',
  height: 'auto',
  'max-width': 'none',
  'max-height': 'none',
  margin: '0',
  'border-radius': '0',
  'align-items': 'stretch',
  transition: 'none',
  animation: 'none',
};
const WRAPPER_STYLES: Record<string, string> = {
  width: '100%',
  height: '100%',
  'max-width': 'none',
  margin: '0',
  'align-self': 'stretch',
  'border-radius': '0',
  transition: 'none',
  animation: 'none',
};
const LIST_STYLES: Record<string, string> = {
  width: '100%',
  'max-width': 'none',
};

const applyStyles = (el: HTMLElement, styles: Record<string, string>, on: boolean) => {
  Object.entries(styles).forEach(([prop, value]) =>
    on ? el.style.setProperty(prop, value, 'important') : el.style.removeProperty(prop),
  );
};

const isPanel = (el: HTMLElement) =>
  el.id === 'optimobile-inbox-ui-root-inbox--right' ||
  el.id === 'optimobile-inbox-ui-root-inbox--left';

// Enforce the full-screen layout on the open inbox panel (mobile only). The panel
// lives in an open Shadow DOM and the SDK keeps re-applying its own (capped)
// styles, so this is re-run whenever the inbox mutates.
const layoutInbox = () => {
  const mobile = window.innerWidth < DESKTOP_MIN_WIDTH;

  let panel: HTMLElement | null = null;
  const wrappers: HTMLElement[] = [];
  const lists: HTMLElement[] = [];
  const walk = (root: Document | ShadowRoot) => {
    root.querySelectorAll<HTMLElement>('*').forEach(el => {
      if (isPanel(el)) panel = el;
      else if (el.classList.contains('optimobile-inbox-inner-wrapper')) wrappers.push(el);
      else if (el.classList.contains('optimobile-message-inbox')) lists.push(el);
      if (el.shadowRoot) walk(el.shadowRoot);
    });
  };
  walk(document);

  // Prefer the SDK's own open state; fall back to the panel's visibility.
  // SDK contract assumptions (re-verify on an Optimove SDK upgrade): the panel id
  // is `optimobile-inbox-ui-root-inbox--{right,left}`, the inner elements use the
  // `optimobile-inbox-inner-wrapper` / `optimobile-message-inbox` classes, the
  // closed panel is hidden via `display:none`, and `isInboxOpen()` reports state.
  const sdkOpen = window.optimoveSDK?.API?.Inbox?.isInboxOpen?.();
  const open =
    typeof sdkOpen === 'boolean' ? sdkOpen : !!panel && getComputedStyle(panel).display !== 'none';
  const active = mobile && open;

  if (panel) applyStyles(panel, ROOT_STYLES, active);
  wrappers.forEach(el => applyStyles(el, WRAPPER_STYLES, active));
  lists.forEach(el => applyStyles(el, LIST_STYLES, active));
};

// The shadow root the Optimove inbox renders into. It exists once the badge is
// rendered, so we can find it on click and observe it for the panel both
// appearing and being re-styled — no timed polling needed.
const findInboxShadowRoot = (): ShadowRoot | null => {
  let found: ShadowRoot | null = null;
  const walk = (root: Document | ShadowRoot) => {
    root.querySelectorAll<HTMLElement>('*').forEach(el => {
      if (el.shadowRoot) {
        if (el.shadowRoot.querySelector('[class*="optimobile-inbox"], [id*="optimobile-inbox"]')) {
          found = el.shadowRoot;
        }
        walk(el.shadowRoot);
      }
    });
  };
  walk(document);
  return found;
};

const OBSERVER_OPTIONS: MutationObserverInit = {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['style', 'class'],
};

const InboxBadge = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const ref = useRef<HTMLDivElement>(null);

  // The Optimove badge renders inside a Shadow DOM where only the small bell icon
  // is clickable. We capture clicks on the whole circle, stop them before they
  // reach the SDK badge (avoiding a double-toggle), toggle the inbox via the SDK
  // API, and keep the mobile full-screen layout applied via an observer.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let observer: MutationObserver | null = null;
    let observedRoot: ShadowRoot | null = null;
    let scheduled = false;

    // Re-apply once per frame; rAF runs before paint, so the SDK's transient
    // styles never get painted (no flicker).
    const scheduleLayout = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        observer?.disconnect();
        layoutInbox();
        if (observedRoot) observer?.observe(observedRoot, OBSERVER_OPTIONS);
      });
    };

    // Observe the inbox shadow root. The observer fires both when the panel
    // mounts (first open) and whenever the SDK re-styles it, re-running layout.
    const observeInbox = () => {
      const root = findInboxShadowRoot();
      if (root && root !== observedRoot) {
        observer?.disconnect();
        observedRoot = root;
        observer = new MutationObserver(scheduleLayout);
        observer.observe(root, OBSERVER_OPTIONS);
      }
      layoutInbox();
    };

    const handleClick = (e: Event) => {
      // The SDK renders the panel inside this wrapper, so this listener also sees
      // clicks inside the open panel. Only the launcher should toggle — let panel
      // clicks (messages, close button, scroll) through to the SDK. composedPath()
      // sees through the shadow DOM.
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      if (path.some(n => n instanceof HTMLElement && isPanel(n))) return;

      e.stopPropagation();
      try {
        window.optimoveSDK?.API?.Inbox?.toggleInbox?.();
      } catch {}
      observeInbox();
    };

    el.addEventListener('click', handleClick, true);
    window.addEventListener('resize', scheduleLayout);
    return () => {
      el.removeEventListener('click', handleClick, true);
      window.removeEventListener('resize', scheduleLayout);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      data-cy="header-inbox"
      aria-label="Inbox button"
      aria-roledescription="Opens notification inbox"
      className={classNames(
        'items-center justify-center cursor-pointer rounded-full bg-blue-light h-[38px] w-[38px] shadow-md transition hover:bg-cyan-600 hover:shadow-lg',
        {
          hidden: !isAuthenticated,
          flex: isAuthenticated,
        },
      )}
      id="optimove_inbox_badge"
    />
  );
};

export default InboxBadge;
